import { Component, Output, EventEmitter, Input, ElementRef, ViewChild } from '@angular/core';
import { TeiasoftModalService } from '../../../shared/services/teiasoft-bucket/teiasoft-modal.service';
import { TeiasoftModalPayload } from '../../models/app/teiasoft-modal.model';

export class TeiasoftModalReturnObject {
  closeFlag?: string;
  parameter?: any;
  notes?: string
}

/**
 * Modal host component.
 *
 * This component subscribes to TeiasoftModalService and renders the modal UI
 * for payloads matching its scope.
 *
 * Usage notes:
 * - mounting this component in a page enables modal rendering for that page
 *   and its descendants;
 * - the service alone is not sufficient: without a mounted host, no modal is shown;
 * - if multiple hosts share the same scope, they can all react to the same payload.
 */
@Component({
  selector: 'teiasoft-modal',
  templateUrl: './teiasoft-modal.component.html',
  styleUrls: ['./teiasoft-modal.component.scss']
})
export class TeiasoftModalComponent {
  @Input() theme: string = 'silver';
  @Input() isMobile: boolean = false;
  /** Hosts only payloads whose scope matches this value. Default scope is 'global'. */
  @Input() scope: string = 'global';
  @Output() onConfirmClick = new EventEmitter<TeiasoftModalReturnObject>();
  @ViewChild('modalContainer', { static: true }) modalContainer?: ElementRef<HTMLElement>;

  // Modal state
  modalType: string = 'info';
  modalTitle: string = 'Modal Title';
  modalMessage: string = 'Modal Message';
  parameter: any;
  scrollVisible: boolean = false;
  top: number = 0;

  // Flags
  flags: {
    close?: string;
    apply?: string;
    discard?: string;
    cancel?: string;
  } = {};

  showNotesInput: boolean = false;
  notesLabel: string = 'Notes';
  notes: string = '';



  constructor(private teiasoftModalService: TeiasoftModalService) {
    this.teiasoftModalService.teiasoftModalStart$.subscribe((res: TeiasoftModalPayload) => {
      if (!res) return;
      const payloadScope = res.scope ?? 'global';
      if (payloadScope !== this.scope) return;

      // Modal config
      this.modalType = res.type;
      this.modalTitle = res.title;
      this.modalMessage = res.message;
      this.parameter = res.parameter;
      this.flags = res.flags || {};
      this.showNotesInput = res.showNotesInput || false;
      this.notesLabel = res.notesLabel || 'Notes';
      this.notes = '';


      // Scroll handling
      const html = document.querySelector('html');
      if (html) {
        this.top = html.scrollTop;
        const modalContainer = this.modalContainer?.nativeElement;
        if (modalContainer) {
          modalContainer.style.top = `${this.top}px`;
        }

        if (this.top > 0) {
          this.scrollVisible = true;
          html.style.overflowY = 'hidden';
          document.body.classList.add('no-scroll');
        }
      }

      setTimeout(() => {
        const modal = this.modalContainer?.nativeElement;
        if (modal) {
          modal.classList.add('tm-visible');
        }
      }, 50);
    });
  }

  onCloseModalClick(): void {
    document.body.classList.remove('no-scroll');

    if (this.scrollVisible) {
      const html = document.querySelector('html');
      if (html) html.style.overflowY = 'auto';
      this.scrollVisible = false;
    }

    this.modalContainer?.nativeElement.classList.remove('tm-visible');
  }

  private emitAction(flag?: string): void {
    const ret: TeiasoftModalReturnObject = {
      closeFlag: flag,
      parameter: this.parameter,
      notes: this.notes
    };
    this.onCloseModalClick();
    this.onConfirmClick.emit(ret);
  }

  onConfirmModalClick(): void {
    this.emitAction(this.flags.close);
  }

  onCancelModalClick(): void {
    if (this.flags.cancel) {
      this.emitAction(this.flags.cancel);
      return;
    }

    this.onCloseModalClick();
  }

  onApplyModalClick(): void {
    this.emitAction(this.flags.apply);
  }

  onDiscardModalClick(): void {
    this.emitAction(this.flags.discard);
  }
}
